import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const CartCtx = createContext(null);
const AuthCtx = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("eterniza_cart") || "[]"); } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("eterniza_cart", JSON.stringify(items));
  }, [items]);

  const add = useCallback((line) => {
    setItems((prev) => {
      const key = `${line.product_id}::${line.photo_id || ""}`;
      const idx = prev.findIndex((p) => `${p.product_id}::${p.photo_id || ""}` === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + line.quantity };
        return copy;
      }
      return [...prev, line];
    });
    setOpen(true);
  }, []);

  const remove = useCallback((idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const setQty = useCallback((idx, qty) => {
    setItems((prev) => prev.map((p, i) => (i === idx ? { ...p, quantity: Math.max(1, qty) } : p)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const total = items.reduce((acc, i) => acc + Number(i.price || 0) * i.quantity, 0);
  const count = items.reduce((acc, i) => acc + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ items, add, remove, setQty, clear, total, count, open, setOpen }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("eterniza_token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("eterniza_token");
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password, kind = "customer") => {
    const path = kind === "admin" ? "/auth/login" : "/customers/login";
    const { data } = await api.post(path, { email, password });
    localStorage.setItem("eterniza_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/customers/register", payload);
    localStorage.setItem("eterniza_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("eterniza_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
